import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const TIPOS_RELACION = [
  { value: 'yo_mismo', label: 'Yo mismo/a' },
  { value: 'tutor_legal', label: 'Tutor/a legal' },
  { value: 'padre', label: 'Padre' },
  { value: 'madre', label: 'Madre' },
  { value: 'responsable', label: 'Responsable' },
  { value: 'tutor_temporal', label: 'Tutor/a temporal' },
  { value: 'acompanante', label: 'Acompanante' },
];

const TIPO_LABEL = Object.fromEntries(TIPOS_RELACION.map(t => [t.value, t.label]));

const SEXO_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
];

const DOC_TYPES = [
  { value: 'DNI', label: 'DNI' },
  { value: 'NIE', label: 'NIE' },
  { value: 'Pasaporte', label: 'Pasaporte' },
];

const GRUPOS_SANGUINEOS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const EMPTY_FORM = {
  firstName: '', lastName1: '', lastName2: '',
  fechaNacimiento: '', sex: '',
  email: '', phone: '', phoneExt: '+34',
  address: '', municipality: '', province: '', country: 'Espana',
  docType: '', docNum: '', docSupport: '', docEmission: '',
  alergias: '', medicacionActual: '', enfermedades: '',
  grupoSanguineo: '', tarjetaSanitaria: '', autorizaMedicacion: false,
  contactoEmergenciaNombre: '', contactoEmergenciaTelefono: '',
  contactoEmergenciaExt: '+34', contactoEmergenciaRelacion: '',
  tipo: 'tutor_legal',
};

export default function MisPersonas() {
  const { token, user } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal crear/editar
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Modal detalle
  const [detailId, setDetailId] = useState(null);

  // Confirmar borrar
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Tabs del formulario
  const [formTab, setFormTab] = useState('personal');

  useEffect(() => { loadPersonas(); }, []);

  async function loadPersonas() {
    setLoading(true);
    try {
      const data = await api.getMyPersonas(token);
      setPersonas(data.personas || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  // ── Yo Mismo ──────────────────────────────────────────────
  const hasYoMismo = personas.some(p => p.relacion?.tipo === 'yo_mismo');

  async function handleCreateYoMismo() {
    setEditId(null);
    setForm({
      ...EMPTY_FORM,
      tipo: 'yo_mismo',
      email: user?.email || '',
    });
    setFormTab('personal');
    setFormError('');
    setShowForm(true);
  }

  // ── Crear persona ─────────────────────────────────────────
  function handleAdd() {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setFormTab('personal');
    setFormError('');
    setShowForm(true);
  }

  // ── Editar persona ────────────────────────────────────────
  function handleEdit(persona) {
    setEditId(persona.id);
    setForm({
      firstName: persona.firstName || '',
      lastName1: persona.lastName1 || '',
      lastName2: persona.lastName2 || '',
      fechaNacimiento: persona.fechaNacimiento || '',
      sex: persona.sex || '',
      email: persona.email || '',
      phone: persona.phone || '',
      phoneExt: persona.phoneExt || '+34',
      address: persona.address || '',
      municipality: persona.municipality || '',
      province: persona.province || '',
      country: persona.country || 'Espana',
      docType: persona.docType || '',
      docNum: persona.docNum || '',
      docSupport: persona.docSupport || '',
      docEmission: persona.docEmission || '',
      alergias: persona.alergias || '',
      medicacionActual: persona.medicacionActual || '',
      enfermedades: persona.enfermedades || '',
      grupoSanguineo: persona.grupoSanguineo || '',
      tarjetaSanitaria: persona.tarjetaSanitaria || '',
      autorizaMedicacion: persona.autorizaMedicacion || false,
      contactoEmergenciaNombre: persona.contactoEmergenciaNombre || '',
      contactoEmergenciaTelefono: persona.contactoEmergenciaTelefono || '',
      contactoEmergenciaExt: persona.contactoEmergenciaExt || '+34',
      contactoEmergenciaRelacion: persona.contactoEmergenciaRelacion || '',
      tipo: persona.relacion?.tipo || 'tutor_legal',
    });
    setFormTab('personal');
    setFormError('');
    setShowForm(true);
  }

  // ── Guardar ───────────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    setFormError('');

    if (!form.firstName?.trim() || !form.lastName1?.trim()) {
      setFormError('Nombre y primer apellido son obligatorios');
      return;
    }
    if (!form.fechaNacimiento) {
      setFormError('La fecha de nacimiento es obligatoria');
      return;
    }
    if (!form.sex) {
      setFormError('El sexo es obligatorio');
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        // Actualizar
        const { tipo, ...updateData } = form;
        await api.updatePersona(token, editId, updateData);
        setSuccess('Persona actualizada correctamente');
      } else if (form.tipo === 'yo_mismo') {
        await api.createYoMismo(token, form);
        setSuccess('Perfil personal creado correctamente');
      } else {
        await api.createPersona(token, form);
        setSuccess('Persona registrada correctamente');
      }
      setShowForm(false);
      await loadPersonas();
    } catch (err) {
      setFormError(err.message);
    }
    setSaving(false);
  }

  // ── Borrar ────────────────────────────────────────────────
  async function handleDelete(id) {
    setDeleting(true);
    try {
      await api.deletePersona(token, id);
      setSuccess('Persona eliminada');
      setConfirmDelete(null);
      await loadPersonas();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(null);
    }
    setDeleting(false);
  }

  // ── Helpers ───────────────────────────────────────────────
  function calcAge(dateStr) {
    if (!dateStr) return null;
    const b = new Date(dateStr);
    const t = new Date();
    let age = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
    return age;
  }

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    // Si cambia la fecha y la persona deja de ser menor, volver a tab válida
    if (field === 'fechaNacimiento' && value) {
      const age = calcAge(value);
      if (age >= 18 && formTab === 'emergencia') {
        setFormTab('salud');
      }
    }
  }

  // Limpiar alertas tras 5 segundos
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // ── Render ────────────────────────────────────────────────
  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  const detailPersona = detailId ? personas.find(p => p.id === detailId) : null;

  return (
    <div className="main-content page-enter">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="section-label">Personas registradas</div>
        <h1>Mis personas</h1>
        <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
          Gestiona las personas que participan en tus inscripciones
        </p>
      </div>

      {/* Alerts */}
      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span> {error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}><span>🌿</span> {success}</div>}

      {/* CTA: Yo Mismo */}
      {!hasYoMismo && (
        <div className="card" style={{
          padding: '1.2rem', marginBottom: '1.2rem',
          borderLeft: '4px solid var(--sage)',
          background: 'rgba(122,171,138,.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>
                Registra tus datos personales
              </div>
              <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                Para participar en eventos necesitas registrarte como persona
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleCreateYoMismo}>
              + Registrarme
            </button>
          </div>
        </div>
      )}

      {/* Acciones */}
      <div style={{ display: 'flex', gap: '.6rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={handleAdd}>
          + Anadir persona
        </button>
      </div>

      {/* Lista de personas */}
      {personas.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
          No tienes personas registradas. Empieza registrandote o anade a un miembro de tu grupo.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
          {personas.map(p => {
            const age = p.edad ?? calcAge(p.fechaNacimiento);
            const isMinor = age !== null && age < 18;
            return (
              <div key={p.id} className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.8rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--white)' }}>
                        {p.nombre || `${p.firstName || ''} ${p.lastName1 || ''}`.trim()}
                      </span>
                      <span className="badge" style={{
                        background: p.relacion?.tipo === 'yo_mismo'
                          ? 'rgba(74,143,168,.2)' : isMinor
                          ? 'rgba(245,158,11,.15)' : 'rgba(122,171,138,.15)',
                        color: p.relacion?.tipo === 'yo_mismo'
                          ? '#4a8fa8' : isMinor
                          ? '#f59e0b' : 'var(--sage)',
                        border: `1px solid ${p.relacion?.tipo === 'yo_mismo'
                          ? 'rgba(74,143,168,.3)' : isMinor
                          ? 'rgba(245,158,11,.3)' : 'rgba(122,171,138,.3)'}`,
                        fontSize: '.65rem',
                      }}>
                        {TIPO_LABEL[p.relacion?.tipo] || p.relacion?.tipo}
                      </span>
                      {isMinor && (
                        <span className="badge badge-amber" style={{ fontSize: '.65rem' }}>
                          Menor
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.3rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {age !== null && <span>{age} anos</span>}
                      {p.sex && <span>{p.sex === 'M' ? 'Masculino' : 'Femenino'}</span>}
                      {p.email && <span>{p.email}</span>}
                      {p.phone && <span>{p.phoneExt || '+34'} {p.phone}</span>}
                    </div>
                    {/* Indicadores de salud */}
                    {(p.alergias || (isMinor && (p.medicacionActual || p.enfermedades))) && (
                      <div style={{ marginTop: '.4rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                        {p.alergias && (
                          <span style={{ fontSize: '.7rem', color: '#f59e0b', background: 'rgba(245,158,11,.1)', padding: '.15rem .5rem', borderRadius: '99px' }}>
                            Alergias
                          </span>
                        )}
                        {isMinor && p.medicacionActual && (
                          <span style={{ fontSize: '.7rem', color: '#3b82f6', background: 'rgba(59,130,246,.1)', padding: '.15rem .5rem', borderRadius: '99px' }}>
                            Medicacion
                          </span>
                        )}
                        {isMinor && p.enfermedades && (
                          <span style={{ fontSize: '.7rem', color: '#8b5cf6', background: 'rgba(139,92,246,.1)', padding: '.15rem .5rem', borderRadius: '99px' }}>
                            Enfermedades
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '.4rem', flexShrink: 0 }}>
                    <button className="btn btn-secondary" onClick={() => setDetailId(p.id)} style={{ fontSize: '.78rem', padding: '.35rem .7rem' }}>
                      Ver
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleEdit(p)} style={{ fontSize: '.78rem', padding: '.35rem .7rem' }}>
                      Editar
                    </button>
                    {p.relacion?.tipo !== 'yo_mismo' && (
                      <button className="btn btn-danger" onClick={() => setConfirmDelete(p)} style={{ fontSize: '.78rem', padding: '.35rem .7rem' }}>
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════ */}
      {/* Modal: Detalle de persona                             */}
      {/* ══════════════════════════════════════════════════════ */}
      {detailPersona && (
        <div className="modal-overlay" onClick={() => setDetailId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem' }}>
                {detailPersona.nombre || `${detailPersona.firstName} ${detailPersona.lastName1}`}
              </h2>
              <button className="btn btn-ghost" onClick={() => setDetailId(null)} style={{ fontSize: '1.2rem' }}>x</button>
            </div>

            {/* Datos personales */}
            <div className="section-label" style={{ marginBottom: '.6rem' }}>Datos personales</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem .8rem', marginBottom: '1rem' }}>
              <FieldDisplay label="Nombre" value={detailPersona.firstName} />
              <FieldDisplay label="Primer apellido" value={detailPersona.lastName1} />
              <FieldDisplay label="Segundo apellido" value={detailPersona.lastName2} />
              <FieldDisplay label="Fecha nacimiento" value={detailPersona.fechaNacimiento} />
              <FieldDisplay label="Edad" value={detailPersona.edad != null ? `${detailPersona.edad} anos` : null} />
              <FieldDisplay label="Sexo" value={detailPersona.sex === 'M' ? 'Masculino' : detailPersona.sex === 'F' ? 'Femenino' : null} />
            </div>

            {/* Contacto */}
            <div className="section-label" style={{ marginBottom: '.6rem' }}>Contacto</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem .8rem', marginBottom: '1rem' }}>
              <FieldDisplay label="Email" value={detailPersona.email} />
              <FieldDisplay label="Telefono" value={detailPersona.phone ? `${detailPersona.phoneExt || '+34'} ${detailPersona.phone}` : null} />
              <FieldDisplay label="Direccion" value={detailPersona.address} />
              <FieldDisplay label="Municipio" value={detailPersona.municipality} />
              <FieldDisplay label="Provincia" value={detailPersona.province} />
              <FieldDisplay label="Pais" value={detailPersona.country} />
            </div>

            {/* Documentacion */}
            {(detailPersona.docType || detailPersona.docNum) && (
              <>
                <div className="section-label" style={{ marginBottom: '.6rem' }}>Documentacion</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem .8rem', marginBottom: '1rem' }}>
                  <FieldDisplay label="Tipo documento" value={detailPersona.docType} />
                  <FieldDisplay label="Numero" value={detailPersona.docNum} />
                  <FieldDisplay label="Soporte" value={detailPersona.docSupport} />
                  <FieldDisplay label="Emision" value={detailPersona.docEmission} />
                </div>
              </>
            )}

            {/* Salud */}
            <div className="section-label" style={{ marginBottom: '.6rem' }}>Salud</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem .8rem', marginBottom: '1rem' }}>
              <FieldDisplay label="Alergias" value={detailPersona.alergias} warn={!!detailPersona.alergias} />
              {/* Campos adicionales solo para menores */}
              {(detailPersona.esMenor || (detailPersona.edad != null && detailPersona.edad < 18) || (detailPersona.fechaNacimiento && calcAge(detailPersona.fechaNacimiento) < 18)) && (
                <>
                  <FieldDisplay label="Enfermedades" value={detailPersona.enfermedades} />
                  <FieldDisplay label="Medicacion actual" value={detailPersona.medicacionActual} />
                  <FieldDisplay label="Grupo sanguineo" value={detailPersona.grupoSanguineo} />
                  <FieldDisplay label="Tarjeta sanitaria" value={detailPersona.tarjetaSanitaria} />
                  <FieldDisplay label="Autoriza medicacion" value={detailPersona.autorizaMedicacion ? 'Si' : 'No'} />
                </>
              )}
            </div>

            {/* Contacto emergencia (solo menores) */}
            {(detailPersona.esMenor || (detailPersona.edad != null && detailPersona.edad < 18) || (detailPersona.fechaNacimiento && calcAge(detailPersona.fechaNacimiento) < 18)) && (
              <div style={{ padding: '.7rem', background: 'rgba(255,255,255,.03)', borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.12)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.4rem' }}>
                  Contacto de emergencia
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.4rem' }}>
                  <FieldDisplay label="Nombre" value={detailPersona.contactoEmergenciaNombre} />
                  <FieldDisplay label="Relacion" value={detailPersona.contactoEmergenciaRelacion} />
                  <FieldDisplay label="Telefono" value={detailPersona.contactoEmergenciaTelefono ? `${detailPersona.contactoEmergenciaExt || '+34'} ${detailPersona.contactoEmergenciaTelefono}` : null} />
                </div>
              </div>
            )}

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
              <button className="btn btn-secondary" onClick={() => { setDetailId(null); handleEdit(detailPersona); }}>
                Editar
              </button>
              <button className="btn btn-ghost" onClick={() => setDetailId(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════ */}
      {/* Modal: Formulario crear/editar                        */}
      {/* ══════════════════════════════════════════════════════ */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
                {editId ? 'Editar persona' : form.tipo === 'yo_mismo' ? 'Mis datos personales' : 'Registrar persona'}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ fontSize: '1.2rem' }}>x</button>
            </div>

            {formError && <div className="alert alert-error" style={{ marginBottom: '.8rem' }}><span>⚠</span> {formError}</div>}

            {/* Tabs del formulario */}
            <div style={{ display: 'flex', gap: '.25rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
              {[
                { id: 'personal', label: 'Personal', icon: '👤' },
                { id: 'contacto', label: 'Contacto', icon: '📱' },
                { id: 'documentacion', label: 'Documentacion', icon: '📄' },
                { id: 'salud', label: 'Salud', icon: '🏥' },
                ...(form.fechaNacimiento && calcAge(form.fechaNacimiento) < 18 ? [
                  { id: 'emergencia', label: 'Emergencia', icon: '🚨' },
                ] : []),
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`btn ${formTab === t.id ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFormTab(t.id)}
                  style={{ fontSize: '.75rem', padding: '.3rem .6rem' }}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSave}>
              {/* ── Tab: Personal ── */}
              {formTab === 'personal' && (
                <div>
                  {/* Tipo de relacion (solo al crear) */}
                  {!editId && form.tipo !== 'yo_mismo' && (
                    <div className="form-group">
                      <label className="form-label">Relacion contigo *</label>
                      <select className="form-select" value={form.tipo} onChange={e => updateField('tipo', e.target.value)} required>
                        {TIPOS_RELACION.filter(t => t.value !== 'yo_mismo').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 .8rem' }}>
                    <div className="form-group">
                      <label className="form-label">Nombre *</label>
                      <input className="form-input" value={form.firstName} onChange={e => updateField('firstName', e.target.value)} required placeholder="Nombre" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Primer apellido *</label>
                      <input className="form-input" value={form.lastName1} onChange={e => updateField('lastName1', e.target.value)} required placeholder="Primer apellido" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Segundo apellido</label>
                      <input className="form-input" value={form.lastName2} onChange={e => updateField('lastName2', e.target.value)} placeholder="Segundo apellido" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Fecha de nacimiento *</label>
                      <input className="form-input" type="date" value={form.fechaNacimiento} onChange={e => updateField('fechaNacimiento', e.target.value)} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sexo *</label>
                      <select className="form-select" value={form.sex} onChange={e => updateField('sex', e.target.value)} required>
                        <option value="">Seleccionar...</option>
                        {SEXO_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {form.fechaNacimiento && (
                    <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginTop: '-.3rem', marginBottom: '.5rem' }}>
                      Edad: {calcAge(form.fechaNacimiento)} anos
                      {calcAge(form.fechaNacimiento) < 18 && (
                        <span style={{ color: '#f59e0b', marginLeft: '.5rem' }}>(menor de edad)</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Tab: Contacto ── */}
              {formTab === 'contacto' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="email@ejemplo.com" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '0 .5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Prefijo</label>
                      <input className="form-input" value={form.phoneExt} onChange={e => updateField('phoneExt', e.target.value)} placeholder="+34" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefono</label>
                      <input className="form-input" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="612345678" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Direccion</label>
                    <input className="form-input" value={form.address} onChange={e => updateField('address', e.target.value)} placeholder="Calle, numero, piso..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 .5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Municipio</label>
                      <input className="form-input" value={form.municipality} onChange={e => updateField('municipality', e.target.value)} placeholder="Municipio" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Provincia</label>
                      <input className="form-input" value={form.province} onChange={e => updateField('province', e.target.value)} placeholder="Provincia" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pais</label>
                      <input className="form-input" value={form.country} onChange={e => updateField('country', e.target.value)} placeholder="Espana" />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab: Documentacion ── */}
              {formTab === 'documentacion' && (
                <div>
                  <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: '1rem' }}>
                    Documentacion de identidad (obligatoria para mayores de 16 anos)
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 .8rem' }}>
                    <div className="form-group">
                      <label className="form-label">Tipo de documento</label>
                      <select className="form-select" value={form.docType} onChange={e => updateField('docType', e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {DOC_TYPES.map(d => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Numero</label>
                      <input className="form-input" value={form.docNum} onChange={e => updateField('docNum', e.target.value)} placeholder="12345678X" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Numero de soporte</label>
                      <input className="form-input" value={form.docSupport} onChange={e => updateField('docSupport', e.target.value)} placeholder="Soporte" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Fecha de emision</label>
                      <input className="form-input" type="date" value={form.docEmission} onChange={e => updateField('docEmission', e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab: Salud ── */}
              {formTab === 'salud' && (
                <div>
                  <div className="form-group">
                    <label className="form-label">Alergias</label>
                    <textarea className="form-input" rows={2} value={form.alergias} onChange={e => updateField('alergias', e.target.value)} placeholder="Alergias alimentarias, medicamentosas... Dejar en blanco si no tiene" style={{ resize: 'vertical' }} />
                  </div>
                  {/* Campos adicionales solo para menores */}
                  {form.fechaNacimiento && calcAge(form.fechaNacimiento) < 18 && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Medicacion actual</label>
                        <textarea className="form-input" rows={2} value={form.medicacionActual} onChange={e => updateField('medicacionActual', e.target.value)} placeholder="Medicacion que toma actualmente y pauta. Dejar en blanco si no toma ninguna" style={{ resize: 'vertical' }} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Enfermedades o condiciones cronicas</label>
                        <textarea className="form-input" rows={2} value={form.enfermedades} onChange={e => updateField('enfermedades', e.target.value)} placeholder="Asma, diabetes, epilepsia, etc. Dejar en blanco si no tiene" style={{ resize: 'vertical' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 .8rem' }}>
                        <div className="form-group">
                          <label className="form-label">Grupo sanguineo</label>
                          <select className="form-select" value={form.grupoSanguineo} onChange={e => updateField('grupoSanguineo', e.target.value)}>
                            <option value="">— Selecciona —</option>
                            {GRUPOS_SANGUINEOS.map(g => (
                              <option key={g} value={g}>{g}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">N° tarjeta sanitaria</label>
                          <input className="form-input" value={form.tarjetaSanitaria} onChange={e => updateField('tarjetaSanitaria', e.target.value)} placeholder="Numero de la tarjeta sanitaria" />
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', fontSize: '.88rem', color: 'var(--white)' }}>
                          <input type="checkbox" checked={form.autorizaMedicacion} onChange={e => updateField('autorizaMedicacion', e.target.checked)}
                            style={{ width: 18, height: 18, accentColor: 'var(--sage)' }} />
                          Autorizo la administracion de medicacion en caso de necesidad
                        </label>
                        <p style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.3rem' }}>
                          Autorizo al equipo a administrar la medicacion indicada y/o medicamentos basicos (paracetamol, ibuprofeno) en caso de necesidad
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Tab: Emergencia (solo menores) ── */}
              {formTab === 'emergencia' && form.fechaNacimiento && calcAge(form.fechaNacimiento) < 18 && (
                <div>
                  <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: '1rem' }}>
                    Persona de contacto en caso de emergencia
                  </p>
                  <div className="form-group">
                    <label className="form-label">Nombre completo</label>
                    <input className="form-input" value={form.contactoEmergenciaNombre} onChange={e => updateField('contactoEmergenciaNombre', e.target.value)} placeholder="Nombre del contacto" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Relacion con la persona</label>
                    <input className="form-input" value={form.contactoEmergenciaRelacion} onChange={e => updateField('contactoEmergenciaRelacion', e.target.value)} placeholder="Padre, madre, tutor..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '0 .5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Prefijo</label>
                      <input className="form-input" value={form.contactoEmergenciaExt} onChange={e => updateField('contactoEmergenciaExt', e.target.value)} placeholder="+34" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefono de emergencia</label>
                      <input className="form-input" value={form.contactoEmergenciaTelefono} onChange={e => updateField('contactoEmergenciaTelefono', e.target.value)} placeholder="612345678" />
                    </div>
                  </div>
                </div>
              )}

              {/* Botones del formulario */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1rem', borderTop: '1px solid rgba(122,171,138,.15)', paddingTop: '1rem' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner" /> Guardando...</> : editId ? 'Guardar cambios' : 'Registrar persona'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════ */}
      {/* Modal: Confirmar eliminar                             */}
      {/* ══════════════════════════════════════════════════════ */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2 style={{ margin: '0 0 .8rem', fontSize: '1.05rem' }}>Confirmar eliminacion</h2>
            <p style={{ color: 'var(--mid)', fontSize: '.9rem', marginBottom: '1.2rem' }}>
              Vas a eliminar a <strong style={{ color: 'var(--white)' }}>{confirmDelete.nombre || confirmDelete.firstName}</strong>.
              Esta accion no se puede deshacer.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)} disabled={deleting}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={() => handleDelete(confirmDelete.id)} disabled={deleting}>
                {deleting ? <><span className="spinner" /> Eliminando...</> : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldDisplay({ label, value, warn }) {
  return (
    <div>
      <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.1rem' }}>
        {label}
      </div>
      <div style={{
        fontSize: '.85rem',
        color: value ? (warn ? '#f59e0b' : 'var(--white)') : 'var(--mid)',
        fontStyle: value ? 'normal' : 'italic',
      }}>
        {value || 'No indicado'}
      </div>
    </div>
  );
}
